import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useCompanyStore, useAnswersStore, usePatientStore } from '../../stores';
import { NavigationUtils } from '../../utils';
import { getPatientFileKey } from '../../utils/patientFiles';
import { MAX_PATIENT_DOCUMENTS_FILES, MAX_PATIENT_DOCUMENT_FILE } from '../../constants/patientFiles';
import SelectEventGroupAndPaymentMethodForm from './SelectEventGroupAndPaymentMethodForm';
import { usePatientFileUpload } from '../../hooks';

const PAYMENT_METHODS_ALLOWED = {
	PARTICULAR: 'PARTICULAR',
	INSURANCE: 'INSURANCE'
};

const skipRemoteUpload = async () => ({ skippedRemote: true });

function SelectEventGroupAndPaymentMethodFormContainer() {
	const { companySettings: { scheduleSettings, settings } } = useCompanyStore();
	const { answers, setAnswer, clearInsurance } = useAnswersStore();
	const { patient, setPatient } = usePatientStore();
	const history = useHistory();

	const insurances = useMemo(
		() =>
			scheduleSettings?.insurance_plans?.filter(
				(insurance) => !answers?.insurance_group || insurance.health_insurance_group_id === ~~answers?.insurance_group
			),
		[answers?.insurance_group, scheduleSettings?.insurance_plans]
	);

	const insuranceCardFiles = useMemo(
		() => (patient?.insurance_card_file ? [patient.insurance_card_file] : []),
		[patient?.insurance_card_file]
	);
	const otherDocumentFiles = useMemo(
		() => (answers?.other_document_file ? [answers.other_document_file] : []),
		[answers?.other_document_file]
	);
	const patientFiles = useMemo(
		() => answers?.patient_files || [],
		[answers?.patient_files]
	);

	const insuranceCard = usePatientFileUpload({
		maxFiles: MAX_PATIENT_DOCUMENT_FILE,
		currentFiles: insuranceCardFiles,
		errorToastMessage: 'Erro ao enviar a carteirinha de convênio.',
		upload: skipRemoteUpload,
		onUploaded: useCallback(
			(files) => setPatient({ patient: { ...patient, insurance_card_file: files[0] } }),
			[patient, setPatient]
		),
		onRemove: useCallback(
			() => setPatient({ patient: { ...patient, insurance_card_file: null } }),
			[patient, setPatient]
		)
	});

	const otherDocument = usePatientFileUpload({
		maxFiles: MAX_PATIENT_DOCUMENT_FILE,
		currentFiles: otherDocumentFiles,
		errorToastMessage: 'Erro ao enviar RG ou CNH.',
		upload: skipRemoteUpload,
		onUploaded: useCallback((files) => setAnswer('other_document_file', files[0]), [setAnswer]),
		onRemove: useCallback(() => setAnswer('other_document_file', null), [setAnswer])
	});

	const patientFilesUpload = usePatientFileUpload({
		maxFiles: MAX_PATIENT_DOCUMENTS_FILES,
		currentFiles: patientFiles,
		errorToastMessage: 'Erro ao enviar solicitações de exames/procedimentos.',
		upload: skipRemoteUpload,
		onUploaded: useCallback((files) => {
			setAnswer('patient_files', [...files, ...(answers?.patient_files || [])]);
		}, [answers?.patient_files, setAnswer]),
		onRemove: useCallback((fileKey) => {
			setAnswer(
				'patient_files',
				(answers?.patient_files || []).filter(
					(file, index) => getPatientFileKey(file, index) !== fileKey
				)
			);
		}, [answers?.patient_files, setAnswer])
	});

	const handlePaymentMethodChange = useCallback(
		(method) => {
			setAnswer('payment_method', method);
			if (method === PAYMENT_METHODS_ALLOWED.PARTICULAR) {
				setAnswer('insurance_group', null);
				setAnswer('insurance', null);
				setAnswer('insurance_number', null);
				setPatient({ patient: { ...patient, insurance_card_file: null } });
				setAnswer('other_document_file', null);
				setAnswer('patient_files', []);
				clearInsurance();
			}
		},
		[clearInsurance, patient, setAnswer, setPatient]
	);

	const handleSubmit = useCallback(() => {
		NavigationUtils.navigate(history, '/select-timegrid');
	}, [history]);

	return (
		<SelectEventGroupAndPaymentMethodForm
			selectedGroup={answers?.event_group || null}
			paymentMethod={answers?.payment_method || null}
			insuranceGroupId={answers?.insurance_group || null}
			insuranceId={answers?.insurance || null}
			insuranceNumber={answers?.insurance_number || ''}
			onNextStepPress={handleSubmit}
			onSelectGroup={(group) => setAnswer('event_group', group)}
			onSelectInsuranceGroup={(id) => setAnswer('insurance_group', id)}
			onSelectPaymentMethod={handlePaymentMethodChange}
			onSelectInsurance={(id) => setAnswer('insurance', id)}
			onInsuranceNumberChange={(value) => {
				const stringValue = typeof value === 'object' && value?.target ? value.target.value : value;
				setAnswer('insurance_number', stringValue);
			}}
			groups={scheduleSettings?.event_groups}
			variant={settings?.theme?.name}
			paymentMethodsAllowed={PAYMENT_METHODS_ALLOWED}
			insurances={insurances}
			showInsurances={!!scheduleSettings?.insurance_plans?.length}
			insuranceGroups={scheduleSettings?.insurance_groups}
			patient={answers?.patient}
			isAnesthetistCompany={settings?.is_anesthetist_company}
			insuranceCard={insuranceCard}
			otherDocument={otherDocument}
			patientFiles={patientFilesUpload}
		/>
	);
}

export default SelectEventGroupAndPaymentMethodFormContainer;
